import { useCallback, useEffect } from 'react';

import { LocationWithOffers, offerKeys, useCreateMerchantActivation } from '@givebacks/api/offers';
import { useQueryClient } from '@tanstack/react-query';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { AxiosError } from 'axios';
import {
  MerchantDetailModal,
  MerchantDetailModalProvider,
  MerchantOfferTermsModal,
  useMerchantDetailModalProvider,
} from 'core/Components';
import { getErrorMessage_v2 } from 'core/Utils';
import { useAtom, useSetAtom } from 'jotai';
import { Box, MerchantHoverCard, Popover, notifications } from 'ui';
import { useClickOutside, useDisclosure } from 'ui/Hooks';

import { MapMarker } from '../../../components/Map/MapMarker/MapMarker';
import { openDetailModalAtom, storeMarkerModalAtom } from './markerModalAtom';
import { useInStoreLocations } from './useInStoreLocations';

type MerchantClickArgs = {
  merchantId?: string;
  locationId?: string;
  type?: 'instore' | 'dining';
};
type LocalMerchantMarkerProps = {
  location: LocationWithOffers;
  onClick: (arg0: MerchantClickArgs) => void;
};

const InStoreMerchantMarker = ({ location, onClick }: LocalMerchantMarkerProps) => {
  const [opened, { close, toggle }] = useDisclosure(false);
  const ref = useClickOutside(close);

  const tagline =
    location.merchant.promotional_type === 'percentage'
      ? `${location.merchant.promotional_amount}% Cash Back`
      : `$${location.merchant.promotional_amount} Cash Back`;

  const handleClick = useCallback(() => {
    onClick({
      locationId: location.uuid,
      merchantId: location.merchant.uuid,
      type: location.merchant.in_store ? 'instore' : 'dining',
    });
  }, [location.merchant.in_store, location.merchant.uuid, location.uuid, onClick]);

  return (
    <AdvancedMarker
      /** This is null on purpose, it provides the component interactions */
      onClick={toggle}
      position={{ lat: location.latitude, lng: location.longitude }}
      title={location.merchant.name}
    >
      <Popover
        shadow="md"
        withArrow
        arrowSize={12}
        opened={opened}
        offset={{ mainAxis: 12, crossAxis: 0 }}
        position="top"
      >
        <Popover.Target>
          <Box ref={ref}>
            <MapMarker color="cobalt" />
          </Box>
        </Popover.Target>

        <MerchantHoverCard
          tagline={tagline}
          logoSrc={location.merchant.logo?.url}
          name={location.merchant.name}
          onClick={handleClick}
        />
      </Popover>
    </AdvancedMarker>
  );
};

const InStoreMerchantMarkerModal = () => {
  const [merchantInfo] = useAtom(storeMarkerModalAtom);
  const setBindOpenModal = useSetAtom(openDetailModalAtom);

  const queryClient = useQueryClient();
  const { setMeta, openModalById } = useMerchantDetailModalProvider({});
  const { mutate: activateMerchant } = useCreateMerchantActivation();

  const handleClick = useCallback(
    (merchantId: string) => {
      if (!merchantId) return;
      activateMerchant(merchantId, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: offerKeys.all });
        },
        onError: (e) => {
          const error = e as Error | AxiosError;
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      });
    },
    [activateMerchant, queryClient]
  );

  useEffect(() => {
    if (merchantInfo) {
      setMeta({
        merchantId: merchantInfo?.merchantId,
        locationId: merchantInfo?.locationId,
        type: merchantInfo?.type,
      });
    }
  }, [handleClick, merchantInfo, setMeta]);

  useEffect(() => {
    // Bind the 'open' fn to the atom
    const openDetail = () => {
      return () => openModalById('Detail');
    };

    setBindOpenModal(openDetail);
  }, [openModalById, setBindOpenModal]);

  return (
    <>
      <MerchantDetailModal handleClick={handleClick} />
      <MerchantOfferTermsModal />
    </>
  );
};

const InStoreMerchantMarkers = () => {
  const { locations } = useInStoreLocations();

  const setModalMerchant = useSetAtom(storeMarkerModalAtom);
  // utilize the bound openModal function from the atom
  const [openModal] = useAtom(openDetailModalAtom);

  const handleMerchantCLick = useCallback(
    (merchant: MerchantClickArgs) => {
      setModalMerchant({ ...merchant });
      if (!openModal) return;
      return openModal();
    },
    [openModal, setModalMerchant]
  );

  if (!locations.length) return null;

  return (
    <>
      {locations.slice(0, 100).map((location) => {
        return (
          <InStoreMerchantMarker
            key={location.uuid}
            location={location}
            onClick={handleMerchantCLick}
          />
        );
      })}
      <MerchantDetailModalProvider meta={{}}>
        <InStoreMerchantMarkerModal />
      </MerchantDetailModalProvider>
    </>
  );
};

export { InStoreMerchantMarkers };
export type { MerchantClickArgs };
